"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import Link from "next/link";
import { Dumbbell, ArrowUpRight, X } from "lucide-react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll, type Variants } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { NAV_LINKS } from "../data/nav-links";
import { useScrollSpy } from "./fx/use-scroll-spy";
import { NavAuthButtons } from "./nav-auth-buttons";
import { NavAuthButtonsMobile } from "./nav-auth-buttons-mobile";

// Stable list of section ids (derived once) for the scroll-spy observer.
const SECTION_IDS = NAV_LINKS.map(link => link.href.replace("#", ""));

// Brand lockup — reused in the island and the drawer header.
const Brand = t0 => {
  const $ = _c(4);
  const {
    onClick
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="relative flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-emerald-500 text-zinc-900 shadow-sm shadow-primary/30 transition-transform group-hover:scale-105"><Dumbbell className="size-[18px]" aria-hidden={true} /></div>;
    t2 = <span className="text-[15px] font-bold tracking-tight text-zinc-900">Gym Mart</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onClick) {
    t3 = <Link href="/" onClick={onClick} className="group flex items-center gap-2.5 transition-opacity hover:opacity-90">{t1}{t2}</Link>;
    $[2] = onClick;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

// Two-line icon whose bars swap length on hover; bottom bar carries brand green.
const MenuLines = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="flex flex-col items-end gap-[5px]"><span className="h-[2px] w-5 rounded-full bg-zinc-900 transition-all duration-300 group-hover:w-3.5" /><span className="h-[2px] w-3.5 rounded-full bg-zinc-900 transition-all duration-300 group-hover:w-5" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// Signature slow, smooth easing (Dennis Snellenberg style) for the menu drawer
// and the top navbar — deliberate ease-in-out, not a snappy spring.
const SMOOTH: [number, number, number, number] = [0.76, 0, 0.24, 1];

// Drawer link list stagger — children cascade in one after another.
const listVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.06
    }
  }
};

// Each menu line is clipped by an overflow-hidden wrapper; the text slides up
// from beneath it — the signature Snellenberg-style masked reveal.
const revealUp: Variants = {
  hidden: {
    y: "120%"
  },
  visible: {
    y: 0,
    transition: {
      duration: 0.32,
      ease: [0.33, 1, 0.68, 1]
    }
  }
};

// The CTA block isn't masked, so it just fades up into place after the links.
const fadeUp: Variants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

// Leading-edge curve for the drawer: the control point bulges out to the left
// (middle reaches in first) then settles flat against the panel edge.
const CURVE_BULGED = "M 100 0 Q 0 50 100 100 Z";
const CURVE_FLAT = "M 100 0 Q 100 50 100 100 Z";
const curveVariants: Variants = {
  hidden: {
    d: CURVE_BULGED
  },
  // Morph in lockstep with the panel (same duration + easing) so the bulge is
  // visible across the whole slide — both in and out.
  visible: {
    d: CURVE_FLAT,
    transition: {
      duration: 0.4,
      ease: SMOOTH
    }
  },
  exit: {
    d: CURVE_BULGED,
    transition: {
      duration: 0.3,
      ease: SMOOTH
    }
  }
};
export const MarketingNav = () => {
  const $ = _c(41);
  const [atTop, setAtTop] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const {
    scrollY
  } = useScroll();
  const activeSection = useScrollSpy(SECTION_IDS, true);
  let t0;
  if ($[0] !== scrollY) {
    t0 = y => {
      const prev = scrollY.getPrevious() ?? 0;
      setAtTop(y < 40);
      if (y > prev && y > 120) {
        setHidden(true);
      } else {
        if (y < prev) {
          setHidden(false);
        }
      }
    };
    $[0] = scrollY;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useMotionValueEvent(scrollY, "change", t0);
  let t1;
  let t2;
  if ($[2] !== open) {
    t1 = () => {
      document.body.style.overflow = open ? "hidden" : "";
      return _temp;
    };
    t2 = [open];
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      x: "100%"
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      x: 0,
      transition: {
        duration: 0.4,
        ease: SMOOTH
      }
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hidden: t3,
      visible: t4,
      exit: {
        x: "100%",
        transition: {
          duration: 0.3,
          ease: SMOOTH
        }
      }
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const panelVariants = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      y: -120,
      opacity: 0
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = hidden && !open ? "-100%" : 0;
  let t8;
  if ($[9] !== t7) {
    t8 = {
      y: t7,
      opacity: 1
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.5,
      ease: SMOOTH
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = atTop ? "bg-transparent py-5" : "border-b border-zinc-200/70 bg-background/80 py-2 shadow-sm backdrop-blur-xl";
  let t11;
  if ($[12] !== t10) {
    t11 = cn("fixed inset-x-0 top-0 z-50 hidden transition-all duration-300 md:block", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Brand />;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setHovered(null);
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== activeSection || $[17] !== hovered) {
    t14 = NAV_LINKS.map(link => <a key={link.href} href={link.href} onMouseEnter={() => setHovered(link.href)} className={cn("relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors", activeSection === link.href.replace("#", "") ? "text-primary" : "text-zinc-600 hover:text-zinc-900")}>{hovered === link.href && <motion.span layoutId="island-pill" className="absolute inset-0 z-0 rounded-full bg-zinc-200/70" transition={{
        type: "spring",
        stiffness: 400,
        damping: 32
      }} />}<span className="relative z-10">{link.label}</span></a>);
    $[16] = activeSection;
    $[17] = hovered;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t14) {
    t15 = <nav className="relative hidden items-center md:flex" onMouseLeave={t13}>{t14}</nav>;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="mx-2 hidden h-5 w-px bg-zinc-300/70 md:block" />;
    t17 = <NavAuthButtons />;
    $[21] = t16;
    $[22] = t17;
  } else {
    t16 = $[21];
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t15) {
    t18 = <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">{t12}<div className="flex items-center gap-1.5">{t15}{t16}{t17}</div></div>;
    $[23] = t15;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t11 || $[26] !== t18 || $[27] !== t8) {
    t19 = <motion.header initial={t6} animate={t8} transition={t9} className={t11}>{t18}</motion.header>;
    $[25] = t11;
    $[26] = t18;
    $[27] = t8;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  let t21;
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      scale: 0,
      opacity: 0
    };
    t21 = {
      scale: 1,
      opacity: 1
    };
    t22 = {
      duration: 0.5,
      ease: SMOOTH
    };
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
  } else {
    t20 = $[29];
    t21 = $[30];
    t22 = $[31];
  }
  let t23;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setOpen(true);
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <motion.div initial={t20} animate={t21} transition={t22} className="fixed right-4 top-4 z-50 md:hidden"><Button variant="outline" size="icon" onClick={t23} aria-label="Open menu" className="group size-12 rounded-full border-zinc-200/70 bg-background/80 shadow-lg shadow-zinc-900/10 ring-1 ring-zinc-900/[0.02] backdrop-blur-xl transition-transform hover:scale-105 active:scale-95"><MenuLines /></Button></motion.div>;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== open) {
    t25 = open && <><motion.div key="backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} onClick={() => setOpen(false)} className="fixed inset-0 z-[60] bg-zinc-900/50 backdrop-blur-sm" /><motion.aside key="panel" variants={panelVariants} initial="hidden" animate="visible" exit="exit" className="fixed inset-y-0 right-0 z-[70] w-full max-w-md"><svg aria-hidden={true} viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-y-0 left-0 h-full w-16 -translate-x-full overflow-visible" style={{
          filter: "drop-shadow(-10px 0 22px rgba(0,0,0,0.12))"
        }}><motion.path variants={curveVariants} fill="#ffffff" /></svg><div className="relative flex h-full flex-col overflow-hidden bg-white shadow-2xl"><div aria-hidden={true} className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-zinc-900/[0.04] blur-3xl" /><div className="relative flex items-center justify-between px-6 py-5"><Brand onClick={() => setOpen(false)} /><Button variant="outline" size="icon" onClick={() => setOpen(false)} aria-label="Close menu" className="size-10 rounded-full border-zinc-200 text-zinc-600 transition-all duration-300 hover:rotate-90 hover:border-zinc-300 hover:text-zinc-900"><X className="size-5" /></Button></div><motion.nav variants={listVariants} initial="hidden" animate="visible" className="relative flex flex-1 flex-col overflow-y-auto px-6 pt-2"><div className="mb-2 overflow-hidden"><motion.span variants={revealUp} className="block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Menu</motion.span></div>{NAV_LINKS.map(link_0 => <div key={link_0.href} className="overflow-hidden border-b border-zinc-100"><motion.a href={link_0.href} variants={revealUp} onClick={() => setOpen(false)} className="group flex items-center gap-4 py-4"><span className="text-[1.75rem] font-semibold tracking-tight text-zinc-900 transition-all duration-300 group-hover:translate-x-2 group-hover:text-primary sm:text-[2.25rem]">{link_0.label}</span><ArrowUpRight className="ml-auto size-6 text-zinc-300 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-primary sm:size-7" /></motion.a></div>)}<motion.div variants={fadeUp} className="mt-auto grid gap-3 py-6"><NavAuthButtonsMobile onNavigate={() => setOpen(false)} /></motion.div></motion.nav></div></motion.aside></>;
    $[34] = open;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] !== t25) {
    t26 = <AnimatePresence>{t25}</AnimatePresence>;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== t19 || $[39] !== t26) {
    t27 = <>{t19}{t24}{t26}</>;
    $[38] = t19;
    $[39] = t26;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  return t27;
};
function _temp() {
  document.body.style.overflow = "";
}